import 'leaflet/dist/leaflet.css';

import L, { type LatLngExpression, type LatLngTuple } from 'leaflet';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import styled from 'styled-components';

import { DepotMarker, FitBounds, numberedIcon, OSM_BASEMAP } from 'components/LeafletMap';
import { GENNEVILLIERS_DEPOT } from 'pages/Tournees/helpers';
import { formatDurationMinutes } from 'helpers/dateTimes';

import { buildZoneColorByReferent, UNASSIGNED_COLOR } from '../constants/approColors';

import {
    buildTourStops,
    coordsOf,
    fillStatus,
    groupRowsBySite,
    haversineKm,
    motifFor,
    regroupementRankLabel,
    STATUS_TOKEN,
    sumMetrics,
    type MetricStatus,
    type PlanningTableRow,
} from '../constants/dynamicDeliveryColumns';
import { useTourRoutes } from '../hooks/useTourRoutes';
import { regroupementKey } from './PlanningTourList';

const formatPercent = (value: number | null | undefined): string => (value == null ? '—' : `${value.toFixed(0)}%`);

type PlanningMapProps = {
    rows: PlanningTableRow[];
    selectedKeys: string[];
    onSelect: (ids: string[]) => void;
    focusedApproIds: string[];
    onFocusAppro: (approId: string) => void;
    // « Ajouter à une tournée existante »: while active, clicking a stop marker picks its regroupement as the
    // insertion target (appro + rank) instead of selecting the stop's columns.
    insertMode: boolean;
    onPickRegroupement: (approId: string, rank: number) => void;
    // Tour edit mode: clicking a stop marker (de)selects its whole regroupement instead of selecting columns.
    editMode: boolean;
    // Selected regroupement key (`${approId}|${rank}`) -> 1-based selection (click) order. Drives the
    // selection-order badge on selected markers in edit mode.
    selectionNumberByKey: Map<string, number>;
    onToggleRegroupement: (approId: string, ranks: number[]) => void;
    // Clic sur le titre de la légende : (dé)focus toutes les tournées d'un coup.
    onToggleAllFocus: () => void;
    // Itinéraire routier réel (API IGN) si vrai ; « vol d'oiseau » (lignes droites, aucun appel réseau) sinon.
    roadRouting: boolean;
};

// A geolocated stop of a tour (one stop = one site), numbered from the SAME shared sequence as the tour
// list (`buildTourStops`) — `number` is the stop's position in the full tour, so list badges and marker
// numbers always match.
type MapStop = {
    key: string;
    siteName: string;
    pos: [number, number];
    color: string;
    number: number;
    rows: PlanningTableRow[];
    kg: number;
};

type ApproTour = {
    approId: string;
    name: string;
    color: string;
    stops: MapStop[];
    // Strict lat/lng pairs (LatLngTuple allows an optional altitude, which haversineKm rejects).
    positions: [number, number][];
    stopCount: number;
};

const DEFAULT_CENTER: LatLngExpression = [48.862, 2.34];
const DEFAULT_ZOOM = 12;

// Compass bearing a→b in degrees (0 = north, clockwise) — used to rotate the route arrows along travel direction.
const bearing = (a: LatLngTuple, b: LatLngTuple): number => {
    const toRad = (deg: number): number => (deg * Math.PI) / 180;
    const dLng = toRad(b[1] - a[1]);
    const lat1 = toRad(a[0]);
    const lat2 = toRad(b[0]);
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180) / Math.PI;
};
const midpoint = (a: LatLngTuple, b: LatLngTuple): LatLngTuple => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

// Darkened variant of a zone colour (#rrggbb) — casing under the focused route, so the contour stays in the
// tour's own hue instead of a flat white/grey.
const darken = (hex: string, factor = 0.55): string => {
    const match = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!match) return hex;
    const value = parseInt(match[1], 16);
    const channel = (shift: number): number => Math.round(((value >> shift) & 0xff) * factor);
    return `#${[16, 8, 0].map((shift) => channel(shift).toString(16).padStart(2, '0')).join('')}`;
};

// Filled disc for a stop (one per site). Numbered disc when it belongs to the focused tour, with a
// Départ/Arrivée pill under the first/last stop.
const dotIcon = (color: string, isSelected: boolean): L.DivIcon => {
    const size = isSelected ? 22 : 16;
    const border = isSelected ? 3 : 2;
    return L.divIcon({
        className: 'planning-map-marker',
        html: `<span style="display:block;width:${size}px;height:${size}px;background:${color};border:${border}px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></span>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2 - 4],
    });
};

const arrowIcon = (color: string, angle: number): L.DivIcon =>
    L.divIcon({
        className: 'planning-route-arrow',
        html: `<div style="transform:rotate(${angle}deg);color:${color};font-size:18px;line-height:1;text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;">▲</div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });

// Tag under the focused tour's stop markers: where the tour starts and where it ends.
const stopTag = (number: number, totalStops: number): string | null => {
    if (number === 1 && number === totalStops) return 'Départ · Arrivée';
    if (number === 1) return 'Départ';
    if (number === totalStops) return 'Arrivée';
    return null;
};

// Leaflet only reacts to WINDOW resize, not to its container resizing on its own. Entering edit mode shrinks
// the map's pane (it shares the row with the side-by-side board), so without this the revealed area would
// stay grey until the next window resize. Recompute on every container resize.
const InvalidateOnResize = (): null => {
    const map = useMap();
    useEffect(() => {
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(map.getContainer());
        return () => observer.disconnect();
    }, [map]);
    return null;
};

export const PlanningMap = ({
    rows,
    selectedKeys,
    onSelect,
    focusedApproIds,
    onFocusAppro,
    insertMode,
    onPickRegroupement,
    editMode,
    selectionNumberByKey,
    onToggleRegroupement,
    onToggleAllFocus,
    roadRouting,
}: PlanningMapProps) => {
    // Tours only show columns to deliver; potential columns are an opt-in overlay (toggle in the legend).
    const deliverRows = useMemo(() => rows.filter((row) => row.isDeliver), [rows]);

    const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
    // Multi-focus: each selected appro draws its own numbered route (compare tours side by side to reassign stops).
    const focused = useMemo(() => new Set(focusedApproIds), [focusedApproIds]);

    // Candidate columns grouped per site (small grey markers): gives the planner the geography of what
    // COULD be added around a tour. Off by default — a few hundred extra markers only when asked for.
    const [showPotentials, setShowPotentials] = useState(false);
    // Légende repliable (elle liste tous les appros + potentielles) : on la réduit à son titre pour libérer la carte.
    const [legendCollapsed, setLegendCollapsed] = useState(false);
    const potentialSites = useMemo(
        () =>
            groupRowsBySite(rows.filter((row) => !row.isDeliver))
                .map((site) => ({ ...site, pos: coordsOf(site.rows[0]) }))
                .filter((site): site is typeof site & { pos: [number, number] } => site.pos !== null),
        [rows],
    );

    // Tour colour = the colour of the zone its deliverer is referent of, shared by the route, markers and legend.
    const approColors = useMemo(() => buildZoneColorByReferent(deliverRows), [deliverRows]);

    // One ordered tour per appro, built from the SAME shared stop sequence as the tour list
    // (`buildTourStops`) — marker numbers and list badges cannot diverge.
    const approTours = useMemo<ApproTour[]>(() => {
        const stopsByAppro = buildTourStops(deliverRows);
        return Array.from(stopsByAppro.entries()).map(([approId, stops]) => {
            const firstRow = stops[0]?.rows[0];
            const color = approColors.get(firstRow?.assignedDeliverer?._id ?? '') ?? UNASSIGNED_COLOR;
            const mapStops: MapStop[] = [];
            stops.forEach((stop, index) => {
                const pos = coordsOf(stop.rows[0]);
                if (!pos) return;
                mapStops.push({
                    key: `${approId}|${stop.siteId}`,
                    siteName: stop.siteName,
                    pos,
                    color,
                    number: index + 1,
                    rows: stop.rows,
                    kg: sumMetrics(stop.rows).kg,
                });
            });
            return {
                approId,
                name: firstRow?.assignedDeliverer?.profile.fullname ?? 'Non assigné',
                color,
                stops: mapStops,
                positions: mapStops.map((stop) => stop.pos),
                stopCount: stops.length,
            };
        });
    }, [deliverRows, approColors]);

    // Real road geometry per tour (IGN API), memoised; falls back to the straight line (`tour.positions`)
    // until it loads, or if the call fails.
    const routesByAppro = useTourRoutes(roadRouting ? approTours : []);
    const lineOf = (tour: ApproTour): LatLngTuple[] => routesByAppro.get(tour.approId)?.positions ?? tour.positions;

    const allPoints = useMemo<LatLngTuple[]>(() => approTours.flatMap((tour) => tour.positions), [approTours]);

    const focusedTours = useMemo(() => approTours.filter((tour) => focused.has(tour.approId)), [approTours, focused]);

    const focusedPoints = useMemo(() => focusedTours.flatMap((tour) => tour.positions), [focusedTours]);
    const fitPoints = focusedPoints.length ? focusedPoints : allPoints;

    const legend = useMemo(
        () =>
            approTours
                .map((tour) => ({ id: tour.approId, name: tour.name, color: tour.color }))
                .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
        [approTours],
    );

    return (
        <MapWrapper>
            <StyledMapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} scrollWheelZoom>
                <TileLayer
                    attribution={`${OSM_BASEMAP.attribution} &middot; Itinéraires &copy; IGN-Géoplateforme`}
                    url={OSM_BASEMAP.url}
                    maxZoom={19}
                />
                <FitBounds points={fitPoints} />
                <InvalidateOnResize />
                <DepotMarker depot={GENNEVILLIERS_DEPOT} />

                {/* Casing under each focused tour: a darker shade of its own zone colour, just wider than the
                    route (8px) so it reads as a thin contour over the tiles. */}
                {focusedTours.map((tour) =>
                    tour.positions.length > 1 ? (
                        <Polyline
                            key={`casing-${tour.approId}`}
                            positions={lineOf(tour)}
                            interactive={false}
                            pathOptions={{ color: darken(tour.color), weight: 8, opacity: 0.95 }}
                        />
                    ) : null,
                )}

                {approTours.map((tour) => {
                    if (tour.positions.length < 2) return null;
                    const isFocused = focused.has(tour.approId);
                    const dimmed = focused.size > 0 && !isFocused;
                    return (
                        <Polyline
                            key={tour.approId}
                            positions={lineOf(tour)}
                            eventHandlers={{ click: () => onFocusAppro(tour.approId) }}
                            pathOptions={{
                                color: tour.color,
                                weight: isFocused ? 6 : 4,
                                opacity: dimmed ? 0.3 : isFocused ? 1 : 0.75,
                            }}
                        />
                    );
                })}

                {/* Directional arrows on each focused appro's route, to convey the delivery order. */}
                {focusedTours.flatMap((tour) =>
                    tour.positions.map((point, index) => {
                        if (index === 0) return null;
                        const prev = tour.positions[index - 1];
                        if (haversineKm(prev, point) < 0.05) return null;
                        return (
                            <Marker
                                key={`arrow-${tour.approId}-${index}`}
                                position={midpoint(prev, point)}
                                icon={arrowIcon(tour.color, bearing(prev, point))}
                                interactive={false}
                            />
                        );
                    }),
                )}

                {approTours.map((tour) => {
                    const isFocusTour = focused.has(tour.approId);
                    const dimmed = focused.size > 0 && !isFocusTour;
                    return tour.stops.map((stop) => {
                        const isSelected = stop.rows.some((row) => selected.has(row._id));
                        const tag = isFocusTour ? stopTag(stop.number, tour.stopCount) : null;
                        // A stop is one site = normally one regroupement; show the distinct ranks just in case.
                        const ranks = [...new Set(stop.rows.map((row) => row.rank))].sort((a, b) => a - b);
                        const referentName = stop.rows[0]?.referentDeliverer?.profile.fullname ?? null;
                        // Edit mode: a selected stop shows its CLICK-order number (priority over the tour number),
                        // even when its tour isn't focused. A stop's ranks share one selection group → one number.
                        const selectionNumber = editMode
                            ? ranks
                                  .map((rank) => selectionNumberByKey.get(regroupementKey(tour.approId, rank)))
                                  .find((value) => value != null)
                            : undefined;
                        return (
                            <Marker
                                key={stop.key}
                                position={stop.pos}
                                icon={
                                    selectionNumber != null
                                        ? numberedIcon(stop.color, selectionNumber, { size: 28, pending: true })
                                        : isFocusTour
                                          ? numberedIcon(stop.color, stop.number, { size: isSelected ? 28 : 24, tag })
                                          : dotIcon(stop.color, isSelected)
                                }
                                opacity={dimmed ? 0.55 : 1}
                                zIndexOffset={isSelected ? 1000 : isFocusTour ? 500 : 0}
                                eventHandlers={{
                                    click: () =>
                                        insertMode
                                            ? // Insert after the WHOLE stop: a multi-rank site's last regroupement.
                                              onPickRegroupement(tour.approId, ranks[ranks.length - 1])
                                            : editMode
                                              ? onToggleRegroupement(tour.approId, ranks)
                                              : onSelect(stop.rows.map((row) => row._id)),
                                }}
                            >
                                {/* No detail popup in edit mode: it would cover neighbouring markers and hinder
                                    quick (de)selection. The click still toggles. */}
                                {!editMode && (
                                    <Popup>
                                        <PopupSite>{stop.siteName}</PopupSite>
                                        <PopupColumns>
                                            {stop.rows.map((row) => (
                                                <PopupColumnRow key={row._id}>
                                                    <PopupColumnName>{row.column?.name ?? '—'}</PopupColumnName>
                                                    <PopupColumnMeta>
                                                        <PopupStock
                                                            $status={fillStatus(
                                                                row.metrics?.realFillRatePercent,
                                                                row.thresholds,
                                                            )}
                                                            title="Stock réel"
                                                        >
                                                            réel {formatPercent(row.metrics?.realFillRatePercent)}
                                                        </PopupStock>
                                                        <PopupStock
                                                            $status={fillStatus(
                                                                row.metrics?.transactionalFillRatePercent,
                                                                row.thresholds,
                                                            )}
                                                            title="Stock transactionnel"
                                                        >
                                                            trans.{' '}
                                                            {formatPercent(row.metrics?.transactionalFillRatePercent)}
                                                        </PopupStock>
                                                    </PopupColumnMeta>
                                                </PopupColumnRow>
                                            ))}
                                        </PopupColumns>
                                        <PopupAppro>
                                            <Swatch style={{ background: tour.color }} />
                                            {tour.name}
                                            {isFocusTour ? ` · Arrêt ${stop.number}/${tour.stopCount}` : ''}
                                        </PopupAppro>
                                        <PopupMetaLine>
                                            Regroupement {ranks.map(regroupementRankLabel).join(', ')}
                                        </PopupMetaLine>
                                        <PopupMetaLine>Appro référent : {referentName ?? '—'}</PopupMetaLine>
                                        <PopupTotal>
                                            {stop.rows.length} colonne{stop.rows.length > 1 ? 's' : ''} ·{' '}
                                            {stop.kg.toFixed(1)} kg
                                        </PopupTotal>
                                    </Popup>
                                )}
                            </Marker>
                        );
                    });
                })}

                {/* Candidate columns (grey circles, one per site): geography of what COULD be added around
                    a tour. Click selects them — the ActionBar's « Ajouter à la livraison » then applies. */}
                {showPotentials &&
                    potentialSites.map((site) => (
                        <CircleMarker
                            key={`potential-${site.siteId}`}
                            center={site.pos}
                            radius={5}
                            pathOptions={{
                                color: '#ffffff',
                                weight: 1.5,
                                fillColor: UNASSIGNED_COLOR,
                                fillOpacity: site.rows.some((row) => selected.has(row._id)) ? 1 : 0.75,
                            }}
                            eventHandlers={{ click: () => onSelect(site.rows.map((row) => row._id)) }}
                        >
                            {!editMode && (
                                <Popup>
                                    <PopupSite>{site.siteName}</PopupSite>
                                    <PopupColumns>
                                        {site.rows.slice(0, 6).map((row) => {
                                            const motif = motifFor(row);
                                            return (
                                                <PopupPotential key={row._id}>
                                                    <PopupColumnName>{row.column?.name ?? '—'}</PopupColumnName>
                                                    <PopupReasons>
                                                        {motif ? (
                                                            <ReasonTag $status={motif.status}>{motif.label}</ReasonTag>
                                                        ) : null}
                                                        <span>
                                                            stock{' '}
                                                            {row.metrics?.realFillRatePercent != null
                                                                ? `${row.metrics.realFillRatePercent.toFixed(0)}%`
                                                                : '—'}
                                                        </span>
                                                        <span>
                                                            {row.metrics?.daysSinceLastDelivery ?? '—'} j sans livraison
                                                        </span>
                                                        <span>rang {row.finalRank}</span>
                                                    </PopupReasons>
                                                </PopupPotential>
                                            );
                                        })}
                                    </PopupColumns>
                                    <PopupTotal>
                                        {site.rows.length} colonne{site.rows.length > 1 ? 's' : ''} potentielle
                                        {site.rows.length > 1 ? 's' : ''}
                                        {site.rows.length > 6 ? ` (dont ${site.rows.length - 6} non listées)` : ''} —
                                        clic = sélection, puis « Ajouter à la livraison »
                                    </PopupTotal>
                                </Popup>
                            )}
                        </CircleMarker>
                    ))}
            </StyledMapContainer>

            {/* The left tour list shows the steps (same stop numbers as the markers); the map keeps a colour
                legend whose entries toggle each appro's route on/off — no duplicate step panel. */}
            {approTours.every((tour) => tour.stops.length === 0) ? (
                <EmptyOverlay>Aucune colonne à livrer géolocalisée pour cette date.</EmptyOverlay>
            ) : (
                <LegendCard>
                    <LegendHeader>
                        <LegendTitle
                            role="button"
                            onClick={onToggleAllFocus}
                            title="Afficher / masquer toutes les tournées"
                            data-test="planning-map-legend-title"
                        >
                            Appros — afficher/masquer une tournée
                        </LegendTitle>
                        <LegendCollapseButton
                            type="button"
                            onClick={() => setLegendCollapsed((current) => !current)}
                            $collapsed={legendCollapsed}
                            aria-label={legendCollapsed ? 'Déplier la légende' : 'Replier la légende'}
                            data-test="planning-map-legend-collapse"
                        >
                            <ChevronDown size={14} />
                        </LegendCollapseButton>
                    </LegendHeader>
                    {!legendCollapsed && (
                        <>
                            {legend.map((entry) => {
                                const route = routesByAppro.get(entry.id);
                                return (
                                    <LegendRow
                                        key={entry.id}
                                        type="button"
                                        onClick={() => onFocusAppro(entry.id)}
                                        $active={focused.has(entry.id)}
                                    >
                                        <Swatch style={{ background: entry.color }} />
                                        {entry.name}
                                        {route && (
                                            <LegendMeta>
                                                {(route.distanceMeters / 1000).toFixed(1)} km ·{' '}
                                                {formatDurationMinutes(route.durationSeconds / 60)}
                                            </LegendMeta>
                                        )}
                                    </LegendRow>
                                );
                            })}
                            <LegendRow
                                type="button"
                                onClick={() => setShowPotentials((current) => !current)}
                                $active={showPotentials}
                                data-test="planning-map-toggle-potentials"
                            >
                                <HollowSwatch />
                                Potentielles ({potentialSites.length} sites)
                            </LegendRow>
                        </>
                    )}
                </LegendCard>
            )}
        </MapWrapper>
    );
};

const MapWrapper = styled.div`
    position: relative;
    flex: 1;
    /* Height floor: the Carte view's height chain is content-driven (no definite viewport height up the tree).
       In edit mode the left columns no longer provide height, so without this floor the map (and the columns,
       which align to it) would collapse to 0. */
    min-height: 420px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 10px;
    overflow: hidden;
    /* Contain Leaflet's high pane/control z-indexes (up to ~700) so they don't paint over modals (e.g. the
       confirm dialog, z-index 50) rendered at the body level — otherwise the popup is stuck behind the map. */
    isolation: isolate;
`;

const StyledMapContainer = styled(MapContainer)`
    height: 100%;
    width: 100%;
`;

const PopupSite = styled.div`
    font-weight: 700;
    margin-bottom: 6px;
`;

const PopupColumns = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 8px;
`;

const PopupColumnRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`;

const PopupColumnName = styled.span`
    word-break: break-word;
`;

const PopupColumnMeta = styled.span`
    display: inline-flex;
    gap: 8px;
    white-space: nowrap;
    font-size: 11px;
`;

// Stock value (real / transactional) coloured against the row's Ops thresholds — same status palette as the table.
const PopupStock = styled.span<{ $status: MetricStatus }>`
    color: ${({ theme, $status }) => theme[STATUS_TOKEN[$status]]};
    font-weight: ${({ $status }) => ($status === 'critical' || $status === 'required' ? 700 : 400)};
    white-space: nowrap;
`;

const PopupMetaLine = styled.div`
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const PopupPotential = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

const PopupReasons = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const ReasonTag = styled.span<{ $status: MetricStatus }>`
    color: ${({ theme, $status }) => theme[STATUS_TOKEN[$status]]};
    font-weight: 700;
`;

const PopupAppro = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
`;

const PopupTotal = styled.div`
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const Swatch = styled.span`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
`;

// Legend swatch of the potential-columns overlay — hollow grey dot, like its map markers.
const HollowSwatch = styled.span`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid ${UNASSIGNED_COLOR};
    flex-shrink: 0;
`;

const LegendCard = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: calc(100% - 24px);
    overflow-y: auto;
    padding: 10px 12px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    font-size: 12px;
    color: ${({ theme }) => theme.textColor};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const LegendHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 2px;
`;

const LegendTitle = styled.div`
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.infoTextColor};
    cursor: pointer;
    user-select: none;

    &:hover {
        color: ${({ theme }) => theme.textColor};
    }
`;

const LegendCollapseButton = styled.button<{ $collapsed: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: ${({ theme }) => theme.infoTextColor};
    transition: transform 0.15s ease;
    transform: rotate(${({ $collapsed }) => ($collapsed ? '-90deg' : '0deg')});

    &:hover {
        color: ${({ theme }) => theme.textColor};
    }
`;

const LegendRow = styled.button<{ $active: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    border: none;
    background: ${({ theme, $active }) => ($active ? theme.backgroundColor : 'transparent')};
    color: inherit;
    font-size: 12px;
    font-weight: ${({ $active }) => ($active ? 700 : 400)};
    text-align: left;
    padding: 3px 6px;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.backgroundColor};
    }
`;

const LegendMeta = styled.span`
    margin-left: auto;
    padding-left: 8px;
    color: ${({ theme }) => theme.infoTextColor};
    font-variant-numeric: tabular-nums;
`;

const EmptyOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.backgroundColor}cc;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 14px;
`;
